import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchStoreProductsPage } from '@/lib/storeProducts';
import { STORE_TYPES } from '@/lib/storeManagement';
import ProductCard from '@/components/store/ProductCard';
import { useCart } from '@/contexts/CartContext';
import { ToastContext } from '@/components/ToastProvider';
import { collection, onSnapshot, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import QuickViewModal from '@/components/store/QuickViewModal';
// Chat UI is now rendered globally in the /store page to ensure always-floating visibility.

export default function CooperativeStore({ userLoggedIn = true, selectedCategory = 'all' }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { addItem } = useCart();
  const { notify } = React.useContext(ToastContext);
  
  // Quick view state
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [showQuickView, setShowQuickView] = useState(false);
  // Chat is handled at the /store page level
  

  // Dynamic categories loaded from Categories Management
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  
  // Cooperative store information
  const [cooperativeStoreId, setCooperativeStoreId] = useState(null);
  const [cooperativeStoreName, setCooperativeStoreName] = useState('Cooperative Store');

  // Function to get cooperative store ID
  const getCooperativeStoreId = async () => {
    try {
      const storesRef = collection(db, 'stores');
      const cooperativeQuery = query(
        storesRef,
        where('type', '==', 'cooperative'),
        limit(1)
      );
      
      const storesSnap = await getDocs(cooperativeQuery);
      
      if (!storesSnap.empty) {
        const store = storesSnap.docs[0];
        const storeId = store.id;
        setCooperativeStoreId(storeId);
        setCooperativeStoreName(store.data().name || 'Cooperative Store');
        return storeId;
      } else {
        // Fallback to 'cooperative' if no store found
        setCooperativeStoreId('cooperative');
        setCooperativeStoreName('Cooperative Store');
        return 'cooperative';
      }
    } catch (error) {
      console.error('Error getting cooperative store ID:', error);
      setCooperativeStoreId('cooperative');
      setCooperativeStoreName('Cooperative Store');
      return 'cooperative';
    }
  };

  // Load cooperative store ID on mount
  useEffect(() => {
    getCooperativeStoreId();
  }, []);


  // Load categories from Categories Management - only for cooperative store
  useEffect(() => {
    if (!cooperativeStoreId) return;

    const unsub = onSnapshot(
      query(
        collection(db, 'storeCategories'),
        where('storeId', '==', cooperativeStoreId)
      ),
      (snapshot) => {
        const cats = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Build flattened category structure - show all subcategories directly
        const dynamicCategories = [
          {
            id: 'all',
            name: 'All Products',
            icon: '🛍️',
            description: 'Browse all cooperative products'
          }
        ];

        // Add only active categories that belong to the cooperative store
        cats.forEach(cat => {
          if (cat.isActive) {
            dynamicCategories.push({
              id: cat.id, // Use the actual Firestore document ID instead of generating from name
              name: cat.name,
              icon: cat.icon || '🏷️',
              originalName: cat.name, // Keep original name for product filtering
              storeSection: cat.storeSection, // Keep for backend sorting
              sortOrder: cat.sortOrder || 999 // Use sortOrder field from database, default to 999 if not set
            });
          }
        });

        // If no store-specific categories found, add default cooperative categories
        if (cats.length === 0) {
          const defaultCategories = [
            {
              id: 'gulay-lowland',
              name: 'Gulay LowLand',
              icon: '🥬',
              originalName: 'Gulay LowLand',
              storeSection: 'fresh',
              sortOrder: 1
            },
            {
              id: 'condiments-spices',
              name: 'Condiments/Spices',
              icon: '🌶️',
              originalName: 'Condiments/Spices',
              storeSection: 'fresh',
              sortOrder: 2
            },
            {
              id: 'rice-grains',
              name: 'Rice & Grains',
              icon: '🌾',
              originalName: 'Rice & Grains',
              storeSection: 'grocery',
              sortOrder: 3
            },
            {
              id: 'dairy-products',
              name: 'Dairy Products',
              icon: '🥛',
              originalName: 'Dairy Products',
              storeSection: 'grocery',
              sortOrder: 4
            }
          ];
          
          defaultCategories.forEach(cat => {
            dynamicCategories.push(cat);
          });
        }

        // Sort categories by sortOrder (1 = first, 2 = second, etc.)
        dynamicCategories.sort((a, b) => {
          // "All Products" always stays first
          if (a.id === 'all') return -1;
          if (b.id === 'all') return 1;
          
          // Sort by sortOrder (lower numbers first)
          return (a.sortOrder || 999) - (b.sortOrder || 999);
        });

        setCategories(dynamicCategories);
        setCategoriesLoading(false);
      },
      (error) => {
        // If error loading store-specific categories, use default categories
        const defaultCategories = [
          {
            id: 'all',
            name: 'All Products',
            icon: '🛍️',
            description: 'Browse all cooperative products'
          },
          {
            id: 'gulay-lowland',
            name: 'Gulay LowLand',
            icon: '🥬',
            originalName: 'Gulay LowLand',
            storeSection: 'fresh',
            sortOrder: 1
          },
          {
            id: 'condiments-spices',
            name: 'Condiments/Spices',
            icon: '🌶️',
            originalName: 'Condiments/Spices',
            storeSection: 'fresh',
            sortOrder: 2
          },
          {
            id: 'rice-grains',
            name: 'Rice & Grains',
            icon: '🌾',
            originalName: 'Rice & Grains',
            storeSection: 'grocery',
            sortOrder: 3
          },
          {
            id: 'dairy-products',
            name: 'Dairy Products',
            icon: '🥛',
            originalName: 'Dairy Products',
            storeSection: 'grocery',
            sortOrder: 4
          }
        ];
        
        setCategories(defaultCategories);
        setCategoriesLoading(false);
      }
    );
    return () => unsub();
  }, [cooperativeStoreId]);



     // Load products based on selected category
   useEffect(() => {
     let mounted = true;
     
     const loadProducts = async () => {
       if (!cooperativeStoreId) {
         setLoading(false);
         return;
       }
       
       setLoading(true);
       try {
         let searchCategories = [];
         
         
         
         // Use flattened categories structure
         if (selectedCategory === 'all') {
           // Load all products
           searchCategories = [];
         } else {
           // Find the selected category
           const selectedCat = categories.find(cat => cat.id === selectedCategory);
           
           if (selectedCat) {
             // This is now a direct category, not a main section
             searchCategories = [selectedCat.originalName || selectedCat.name];
           } else {
             searchCategories = [];
           }
         }
         
         let allProducts = [];
         
         if (searchCategories.length === 0) {
            // Load all cooperative products from both generic and specific collections
            const [genericResults, specificResults] = await Promise.all([
              fetchStoreProductsPage({ 
                storeType: STORE_TYPES.COOPERATIVE,
                pageSize: 100 
              }),
              fetchStoreProductsPage({ 
                storeType: STORE_TYPES.COOPERATIVE,
                storeId: cooperativeStoreId,
                pageSize: 100 
              })
            ]);
            
            // Combine products from both collections, avoiding duplicates
            const seenIds = new Set();
            allProducts = [];
            
            // Add generic cooperative products
            genericResults.items.forEach(product => {
              if (!seenIds.has(product.id)) {
                allProducts.push(product);
                seenIds.add(product.id);
              }
            });
            
            // Add specific cooperative store products
            specificResults.items.forEach(product => {
              if (!seenIds.has(product.id)) {
                allProducts.push(product);
                seenIds.add(product.id);
              }
            });
          } else {
           // Load products for the selected category from both collections
            for (const category of searchCategories) {
              try {
                // Try exact match first from both collections
                const [genericResults, specificResults] = await Promise.all([
                  fetchStoreProductsPage({ 
                    storeType: STORE_TYPES.COOPERATIVE,
                    category: category, 
                    pageSize: 100 
                  }),
                  fetchStoreProductsPage({ 
                    storeType: STORE_TYPES.COOPERATIVE,
                    storeId: cooperativeStoreId,
                    category: category, 
                    pageSize: 100 
                  })
                ]);
                
                let items = [...genericResults.items, ...specificResults.items];
                
                // If no exact match, try case-insensitive search from both collections
                if (items.length === 0) {
                  const [allGenericItems, allSpecificItems] = await Promise.all([
                    fetchStoreProductsPage({ 
                      storeType: STORE_TYPES.COOPERATIVE,
                      pageSize: 100 
                    }),
                    fetchStoreProductsPage({ 
                      storeType: STORE_TYPES.COOPERATIVE,
                      storeId: cooperativeStoreId,
                      pageSize: 100 
                    })
                  ]);
                  
                  const allItems = [...allGenericItems.items, ...allSpecificItems.items];
                  
                  // Filter products by case-insensitive category matching
                  items = allItems.filter(product => 
                    product.categories && product.categories.some(productCat => 
                      productCat.toLowerCase().includes(category.toLowerCase()) ||
                      category.toLowerCase().includes(productCat.toLowerCase()) ||
                      // Also check if the category name contains the product category
                      productCat.toLowerCase().includes(category.toLowerCase().replace(/[^a-z0-9]/g, '')) ||
                      category.toLowerCase().replace(/[^a-z0-9]/g, '').includes(productCat.toLowerCase())
                    )
                  );
                }
                
                // Remove duplicates
                const seenIds = new Set();
                const uniqueItems = items.filter(product => {
                  if (seenIds.has(product.id)) {
                    return false;
                  }
                  seenIds.add(product.id);
                  return true;
                });
                
                allProducts = [...allProducts, ...uniqueItems];
              } catch (error) {
                console.warn('Error loading products for category:', category, error);
              }
            }
           
            // Remove duplicates based on product ID
            allProducts = allProducts.filter((product, index, self) => 
              index === self.findIndex(p => p.id === product.id)
            );
          }
          
          if (mounted) {
            setProducts(allProducts);
          }
       } catch (error) {
 
         if (mounted) {
           notify('Error loading products', 'error');
         }
       } finally {
         if (mounted) {
           setLoading(false);
         }
       }
     };
 
     loadProducts();
     return () => { mounted = false; };
   }, [selectedCategory, cooperativeStoreId, notify]);

  // Filter products by search term
  const filteredProducts = products.filter(product => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      product.name?.toLowerCase().includes(searchLower) ||
      product.sku?.toLowerCase().includes(searchLower) ||
      product.categories?.some(cat => cat.toLowerCase().includes(searchLower))
    );
  });

  const handleAddToCart = (product) => {
    // Use the quantity passed from ProductCard, fallback to quantityStep or 1
    const qty = product.qty || product.quantityStep || 1;
    addItem({ 
      ...product, 
      qty: qty,
      storeType: 'cooperative',
      storeId: cooperativeStoreId || 'cooperative'
    });
    notify(`${product.name} added to cart!`, 'success');
  };

  const handleProductClick = (product) => {
    // Navigate to product detail page
    navigate(`/store/${product.id}`);
  };

  const handleQuickView = (product) => {
    setQuickViewProduct(product);
    setShowQuickView(true);
  };

  const getCategoryIcon = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.icon || '🛍️';
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || 'All Products';
  };

     return (
     <div>


             {/* Products Grid */}
       {loading ? (
         <div className="flex justify-center items-center py-8">
           <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
         </div>
       ) : filteredProducts.length === 0 ? (
         <div className="text-center py-8">
           <div className="text-4xl mb-2">🛍️</div>
           <p className="text-gray-600 text-sm">No products found</p>
         </div>
       ) : (
         <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
           {filteredProducts.map((product) => (
             <ProductCard
               key={product.id}
               p={product}
               onClick={() => handleProductClick(product)}
               onAdd={(itemWithQty) => handleAddToCart(itemWithQty)}
               showAdd={userLoggedIn}
               showQuick={true}
               onQuick={() => handleQuickView(product)}
               userLoggedIn={userLoggedIn}
             />
           ))}
                   </div>
        )}
        
        {/* Quick View Modal */}
        <QuickViewModal
          product={quickViewProduct}
          isOpen={showQuickView}
          onClose={() => {
            setShowQuickView(false);
            setQuickViewProduct(null);
          }}
        />

        {/* Floating chat is rendered in Stores.jsx via a portal */}
      </div>
    );
  }
